import { IconProp } from '@fortawesome/fontawesome-svg-core'
import React, { useRef, useState } from 'react'
import { useMediaQuery } from 'react-responsive'
import { isTabletOrSmallDesktopQuery } from '../../utils'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Badge, Overlay, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { IEvent } from '../../store/events/interfaces'
import { Placement } from 'react-bootstrap/Overlay'
import { IProgram } from '../../store/program/interfaces'
import moment from 'moment'

const InfoBadge = (props: {
  icons: IconProp[]
  variant: string
  tooltip: string
  show: boolean
  hasRegistration?: boolean
  setShow: (prevState: boolean) => void
  tooltipPlacement?: Placement
}) => {
  const target = useRef<HTMLButtonElement>(null)
  const { icons, tooltip, show, setShow, variant, hasRegistration, tooltipPlacement } = props
  const isTabletOrSmallDesktop = useMediaQuery(isTabletOrSmallDesktopQuery)

  const placement = tooltipPlacement ? tooltipPlacement : 'auto'

  return (
    <>
      {isTabletOrSmallDesktop ? (
        <>
          <button
            className={`btn btn-sm btn-${
              hasRegistration ? 'outline-light' : variant
            } mr-2 info-badge`}
            ref={target}
            onClick={() => setShow(!show)}
            onBlur={() => (show ? setShow(!show) : null)}
            aria-label="Anzeige des zugehörigen Tooltips"
          >
            {icons.map((i) => (
              <FontAwesomeIcon
                icon={i}
                aria-label={tooltip}
                data-toggle="tooltip"
                role="tooltip"
                aria-hidden="true"
                aria-live="polite"
                tabIndex={0}
                key={`${i}`}
              />
            ))}
          </button>
          {target && target.current && (
            <Overlay target={target.current} show={show} placement={placement}>
              {(props: any) => <Tooltip {...props}>{tooltip}</Tooltip>}
            </Overlay>
          )}
        </>
      ) : (
        <>
          <OverlayTrigger
            placement={placement}
            overlay={<Tooltip id="tooltip">{tooltip}</Tooltip>}
            trigger={['hover', 'focus']}
          >
            <span
              aria-label={tooltip}
              className="mr-1"
              data-toggle="tooltip"
              role="tooltip"
              aria-hidden="true"
              aria-live="polite"
            >
              {icons.map((i) => (
                <FontAwesomeIcon icon={i} tabIndex={0} key={`${i}`} />
              ))}
            </span>
          </OverlayTrigger>{' '}
        </>
      )}
    </>
  )
}
type InfoBadgeData = {
  icons: IconProp[]
  variant: string
  tooltip: string
  predicate: (event: IEvent) => boolean
}
const INFO_BADGES: InfoBadgeData[] = [
  {
    icons: ['ban'],
    variant: 'outline-dark',
    tooltip: 'Abgesagt',
    predicate: (event) => event.canceled,
  },
  {
    icons: ['star'],
    variant: 'outline-dark',
    tooltip: 'Nur für Sonderkunden',
    predicate: (event) => event.is_only_for_special_customers,
  },
  {
    icons: ['universal-access'],
    variant: 'outline-dark',
    tooltip: 'Allgemein barrierefrei/behindertengerecht',
    predicate: (event) => event.supported_disabilities.includes('general'),
  },
  {
    icons: ['wheelchair-move'],
    variant: 'outline-dark',
    tooltip: 'Unterstützt Gehbeeinträchtigte',
    predicate: (event) => event.supported_disabilities.includes('wheelchair'),
  },
  {
    icons: ['eye-low-vision'],
    variant: 'outline-dark',
    tooltip: 'Unterstützt Sehbbeeinträchtigte',
    predicate: (event) => event.supported_disabilities.includes('blind'),
  },
  {
    icons: ['ear-deaf'],
    variant: 'outline-dark',
    tooltip: 'Unterstützt Hörbeeinträchtigte',
    predicate: (event) => event.supported_disabilities.includes('deaf'),
  },
  {
    icons: ['brain'],
    variant: 'outline-dark',
    tooltip: 'Unterstützt geistig Beeinträchtigte',
    predicate: (event) => event.supported_disabilities.includes('intellectual'),
  },
  {
    icons: ['book-open-reader'],
    variant: 'outline-dark',
    tooltip: 'Unterstützt Lernbeeinträchtigte',
    predicate: (event) => event.supported_disabilities.includes('learning'),
  },
  {
    icons: ['users'],
    variant: 'outline-dark',
    tooltip: 'Unterstützt Entwicklungsbeeinträchtigte',
    predicate: (event) =>
      event.supported_disabilities.includes('developmental'),
  },
  {
    icons: ['person-cane'],
    variant: 'outline-dark',
    tooltip: 'Unterstützt Körperlichbeeinträchtigte',
    predicate: (event) => event.supported_disabilities.includes('physical'),
  },
  {
    icons: ['file'],
    variant: 'outline-dark',
    tooltip: 'Hat Anhänge',
    predicate: (event) => event.attachments.length > 0,
  },
  {
    icons: ['user-friends'],
    variant: 'outline-dark',
    tooltip: 'Nur für Erwachsene',
    predicate: (event) => event.is_only_for_adults,
  },
  {
    icons: ['venus'],
    variant: 'outline-dark',
    tooltip: 'Nur für Mädchen',
    predicate: (event) => event.allowed_genders === 'w',
  },
  {
    icons: ['mars'],
    variant: 'outline-dark',
    tooltip: 'Nur für Jungs',
    predicate: (event) => event.allowed_genders === 'm',
  },
  {
    icons: ['neuter'],
    variant: 'outline-dark',
    tooltip: 'Nur für Diverse',
    predicate: (event) => event.allowed_genders === 'd',
  },
  {
    icons: ['neuter', 'mars'],
    variant: 'outline-dark',
    tooltip: 'Nur für Diverse und Jungs',
    predicate: (event) => event.allowed_genders === 'md',
  },
  {
    icons: ['neuter', 'venus'],
    variant: 'outline-dark',
    tooltip: 'Nur für Diverse und Mädchen',
    predicate: (event) => event.allowed_genders === 'wd',
  },
  {
    icons: ['mars', 'venus'],
    variant: 'outline-dark',
    tooltip: 'Nur für Jungs und Mädchen',
    predicate: (event) => event.allowed_genders === 'mw',
  },
]
export const InfoBadges = (props: {
  event: IEvent
  hasRegistration?: boolean
  tooltipPlacement?: Placement
  program?: IProgram
}) => {
  const { event, hasRegistration, tooltipPlacement, program } = props
  const requiredBadges = INFO_BADGES.filter((badgeData) =>
    badgeData.predicate(event)
  )

  // add tags (Kategorien as a Badge)
  if (event.tags && event.tags.length >= 1) {
    requiredBadges.push({
      icons: ['tags'],
      variant: 'outline-dark',
      tooltip: event.tags.join(', '),
      predicate: () => true,
    })
  }

  // add first_registration_date badge
  if (event.first_registration_date && program && program.start_registration_date && new Date(event.first_registration_date) > new Date(program.start_registration_date)) {
    requiredBadges.push({
      icons: ['calendar-check'],
      variant: 'outline-dark',
      tooltip: `Anmeldestart: ${moment(event.first_registration_date).format('DD.MM.YYYY')}`,
      predicate: () => true,
    })
  }

  // add last_registration_date badge
  if (event.last_registration_date && program && (!program.last_registration_date || new Date(event.last_registration_date) < new Date(program.last_registration_date))) {
    requiredBadges.push({
      icons: ['calendar-alt'],
      variant: 'outline-dark',
      tooltip: `Anmeldeschluss: ${moment(event.last_registration_date).format('DD.MM.YYYY')}`,
      predicate: () => true,
    })
  }

  const [shows, setShows] = useState<boolean[]>(
    new Array(requiredBadges.length).fill(false)
  )

  const setShow = (idx: number) => (value: boolean) =>
    setShows(shows.map((show: boolean, key: number) => key === idx && value))

  return (
    <>
      {requiredBadges.map((badgeData, key) => (
        <InfoBadge
          hasRegistration={hasRegistration}
          key={key}
          show={shows[key]}
          setShow={setShow(key)}
          tooltipPlacement={tooltipPlacement}
          {...badgeData}
        />
      ))}
    </>
  )
}
export const PriceBadge = (props: { event: IEvent }) => {
  const { event } = props
  const priceText = event.has_external_payment
    ? event.price + '€ externe Bezahlung'
    : event.price + '€'
  const positivePriceValue = event.price > 0

  return (
    <>
      {positivePriceValue && (
        <>
          <Badge className="badge-pill badge-info">{priceText}</Badge>
        </>
      )}
    </>
  )
}